import { ChatInterface } from "@/components/Chat/ChatInterface";
import { ErrorFallback } from "@/components/ui";
import { useAppInitialization } from "@/hooks/useAppInitialization";
import { useNewChat } from "@/hooks/useNewChat";
import { useToast } from "@/hooks/useToast";
import { useAuthStore, useThreadStore } from "@/store";
import { useCanvas } from "@/contexts/CanvasContext";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation, useNavigate, useParams } from "react-router-dom";

/**
 * ChatPage - Production version with optimized streaming support
 *
 * Features:
 * - Simplified initialization
 * - Clean state management
 * - Optimized message rendering with stable file references
 * - Real-time streaming content updates
 */
export function ChatPage({ isNewChat = false, overrideThreadId = null }) {
  const user = useAuthStore((state) => state.user);
  const { userId, initialized: authInitialized } = useAuthStore();
  const { isLoading, isInitialized } = useAppInitialization();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Ref to track if we've already shown an error for a thread ID to prevent duplicates
  const shownErrorForThreadRef = useRef(new Set());

  // Thread management - handle both /chat/new and /chat/:chatId routes
  // Use overrideThreadId if provided (e.g., when rendered in background during modal)
  const { chatId: urlChatId, threadId: urlThreadId } = useParams();
  const chatId = overrideThreadId || urlChatId;
  const threadId = overrideThreadId || urlThreadId;

  // CRITICAL FIX: Check if we're on /chat/new BEFORE reading params to avoid race conditions
  // Use more robust route detection to prevent race conditions during navigation
  const isOnNewChatRoute =
    isNewChat ||
    location.pathname === "/chat/new" ||
    location.pathname.endsWith("/chat/new");

  // Only read activeThreadId if we're NOT on the new chat route
  // Also ensure we filter out "new" as a thread ID
  const activeThreadId = isOnNewChatRoute
    ? null
    : chatId === "new" || threadId === "new"
      ? null
      : chatId || threadId;

  // Canvas state for route-based cleanup
  const { canvasState, closeCanvas } = useCanvas();

  // Close canvas when navigating to /chat/new or switching threads
  useEffect(() => {
    if (!canvasState.isOpen) return;

    const canvasThreadId = canvasState.thread_id;
    const shouldClose = isOnNewChatRoute ||
                       (canvasThreadId && canvasThreadId !== activeThreadId);

    if (shouldClose) {
      // Check if we're navigating to /chat/new and thread is already cleared
      // This indicates useNewChat already handled the confirmation, so just close canvas
      const { currentThreadId, messages } = useThreadStore.getState();
      const threadAlreadyCleared = isOnNewChatRoute &&
                                   currentThreadId === null &&
                                   (!messages || messages.length === 0);

      // Check for unsaved changes
      if (canvasState.isDirty && !threadAlreadyCleared) {
        // Only show confirmation if thread hasn't been cleared yet
        // (threadAlreadyCleared means useNewChat already handled it)
        const confirmed = window.confirm(
          "You have unsaved changes in the canvas. Are you sure you want to close it?"
        );
        if (!confirmed) {
          // User cancelled, navigate back to previous thread
          if (canvasThreadId && canvasThreadId !== activeThreadId) {
            navigate(`/chat/${canvasThreadId}`, { replace: true });
          }
          return;
        }
      }
      closeCanvas();
    }
  }, [isOnNewChatRoute, activeThreadId, canvasState.isOpen, canvasState.isDirty, canvasState.thread_id, closeCanvas, navigate]);

  // Scroll to image from hash fragment (for "Open in Thread" from library)
  useEffect(() => {
    if (!isInitialized || !activeThreadId) return;

    // Check if there's a specific image ID to highlight in hash fragment
    const hash = location.hash.replace("#", "");
    const match = hash.match(/imageId=([^&]+)/);
    const targetImageId = match ? match[1] : null;

    if (!targetImageId) return;

    let scrolled = false;
    let pollInterval = null;

    const attemptScroll = () => {
      if (scrolled) return true;

      // Look for an image container with the matching data-image-id
      const imageContainer = document.querySelector(
        `[data-image-id="${targetImageId}"]`
      );

      if (!imageContainer) return false;

      // Find the scrollable container (chat messages area)
      let scrollContainer = imageContainer.parentElement;
      while (scrollContainer && scrollContainer !== document.body) {
        const style = window.getComputedStyle(scrollContainer);
        const overflowY = style.overflowY;
        const overflow = style.overflow;

        if (
          overflowY === "auto" ||
          overflowY === "scroll" ||
          overflow === "auto" ||
          overflow === "scroll"
        ) {
          break;
        }
        scrollContainer = scrollContainer.parentElement;
      }

      if (!scrollContainer || scrollContainer === document.body) {
        scrollContainer = document.documentElement;
      }

      // Disable smooth scrolling temporarily
      const elementsToDisable = [
        scrollContainer,
        document.documentElement,
        document.body,
      ];
      const originalBehaviors = elementsToDisable.map((el) => {
        const original = el?.style?.scrollBehavior;
        if (el) el.style.scrollBehavior = "auto";
        return original;
      });

      // Calculate scroll position to center the image
      const imageRect = imageContainer.getBoundingClientRect();
      const scrollContainerRect = scrollContainer.getBoundingClientRect();
      const relativeTop = imageRect.top - scrollContainerRect.top;
      const targetScrollTop =
        scrollContainer.scrollTop +
        relativeTop -
        scrollContainer.clientHeight / 2 +
        imageRect.height / 2;

      // Lock scrolling to prevent auto-scroll-to-bottom from interfering
      const originalScrollLock = scrollContainer.dataset.scrollLocked;
      scrollContainer.dataset.scrollLocked = "true";

      // Scroll with requestAnimationFrame to ensure it happens after other scroll operations
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = targetScrollTop;

        // Remove scroll lock after a delay
        setTimeout(() => {
          if (originalScrollLock) {
            scrollContainer.dataset.scrollLocked = originalScrollLock;
          } else {
            delete scrollContainer.dataset.scrollLocked;
          }
        }, 1000);
      });

      // Re-enable smooth scrolling
      setTimeout(() => {
        elementsToDisable.forEach((el, i) => {
          if (el) el.style.scrollBehavior = originalBehaviors[i] || "";
        });
      }, 200);

      // Highlight the image container
      imageContainer.style.transition =
        "box-shadow 0.5s ease, transform 0.3s ease";
      imageContainer.style.boxShadow = "0 0 0 4px rgba(147, 51, 234, 0.5)";
      imageContainer.style.transform = "scale(1.02)";
      setTimeout(() => {
        imageContainer.style.boxShadow = "";
        imageContainer.style.transform = "";
      }, 2000);

      scrolled = true;
      if (pollInterval) clearInterval(pollInterval);
      return true;
    };

    // Poll for the image to appear in the DOM (handles lazy loading)
    let pollAttempts = 0;
    const maxPollAttempts = 30; // 6 seconds max

    pollInterval = setInterval(() => {
      pollAttempts++;

      const currentMessages =
        useThreadStore.getState().threadMessages.get(activeThreadId) || [];

      if (currentMessages.length > 0) {
        if (attemptScroll()) {
          clearInterval(pollInterval);
        }
      }

      if (pollAttempts >= maxPollAttempts) {
        clearInterval(pollInterval);
      }
    }, 200);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [location.hash, isInitialized, activeThreadId]);

  useEffect(() => {
    // Clear the error tracking when we navigate to a new chat
    if (isOnNewChatRoute) {
      shownErrorForThreadRef.current.clear();
      if (isInitialized) {
        const { clearCurrentThread, setCurrentThreadId } =
          useThreadStore.getState();
        clearCurrentThread();
        setCurrentThreadId(null);
      }
      // Early return - don't try to fetch thread when on /chat/new
      return;
    }

    // Only fetch thread if we have a valid thread ID and we're not on /chat/new
    // Add extra validation: ensure activeThreadId is not null, not "new", and looks like a valid UUID
    if (
      activeThreadId &&
      activeThreadId !== "new" &&
      isInitialized &&
      // Basic UUID format check to prevent invalid fetches
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        activeThreadId
      )
    ) {
      // Handle thread loading for /chat/:chatId routes
      const { setCurrentThreadId, fetchThread } = useThreadStore.getState();
      fetchThread(activeThreadId)
        .then((thread) => {
          if (thread) {
            setCurrentThreadId(activeThreadId);
            // Clear error tracking for successful fetch
            shownErrorForThreadRef.current.delete(activeThreadId);
          } else {
            // CRITICAL FIX: Before showing "chat not found", check if this might be an optimistic thread
            // that's still being created in the database
            const {
              currentThreadId: storeThreadId,
              optimisticMessages,
              threads,
            } = useThreadStore.getState();
            const hasOptimisticMessages =
              optimisticMessages?.has(activeThreadId);
            const hasOptimisticThread = threads?.some(
              (t) => t.id === activeThreadId && t.metadata?.isOptimistic
            );

            // If the current thread ID matches or we have optimistic data, don't redirect
            if (
              storeThreadId === activeThreadId ||
              hasOptimisticMessages ||
              hasOptimisticThread
            ) {
              console.log(
                `[ChatPage] Thread ${activeThreadId} not found in DB but appears to be optimistic, waiting...`
              );
              setCurrentThreadId(activeThreadId);
              return;
            }

            // Only show error if we haven't shown one for this thread ID yet
            if (!shownErrorForThreadRef.current.has(activeThreadId)) {
              shownErrorForThreadRef.current.add(activeThreadId);
              // Thread doesn't exist, redirect to new chat with user feedback
              console.log(
                `[ChatPage] Thread ${activeThreadId} not found, redirecting to /chat/new`
              );
              toast({
                title: "Chat not found",
                description:
                  "This chat may have been deleted or doesn't exist.",
                variant: "destructive",
              });
              navigate("/chat/new", { replace: true });
            }
          }
        })
        .catch((error) => {
          // Only show error if we haven't shown one for this thread ID yet
          if (!shownErrorForThreadRef.current.has(activeThreadId)) {
            shownErrorForThreadRef.current.add(activeThreadId);
            console.error(`[ChatPage] Error fetching thread:`, error);
            toast({
              title: "Error loading chat",
              description: "Unable to load this chat. Please try again.",
              variant: "destructive",
            });
            // On error, redirect to new chat
            navigate("/chat/new", { replace: true });
          }
        });
    }
  }, [isOnNewChatRoute, activeThreadId, isInitialized, navigate, toast]);

  // Use centralized new chat hook
  const { handleNewChat } = useNewChat();

  const handleError = useCallback((error) => {
    console.error("[ChatPage] Error:", error.message || "Something went wrong");
  }, []);

  const handleReset = useCallback(() => {
    // Simple reset - just reload
    window.location.reload();
  }, []);

  // Show loading state while initializing
  if (isLoading || !isInitialized) {
    return (
      <div className="h-screen flex flex-col items-center justify-center">
        {isLoading && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/20 backdrop-blur-[2px]">
            <div className="w-8 h-8 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden">
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={handleError}
        onReset={handleReset}
        className="h-full w-full overflow-hidden"
      >
        <ChatInterface
          onNewChat={handleNewChat}
          isLoading={false}
          isLimitedMode={!user}
          disableTools={!user}
          disableFileUpload={!user}
          onShowAuthOverlay={() => {}}
        />
      </ErrorBoundary>
    </div>
  );
}

ChatPage.propTypes = {
  isNewChat: PropTypes.bool,
  overrideThreadId: PropTypes.string,
};

export default ChatPage;
